/* Copyright 2025 Supovia LLC */
import getApiUrl from '@supovia/shared/getApiUrl.js'
import { getApiKey } from './apiKey.js'
import { getChatAccessToken, getChatEmbedOrigin } from './chatAuthorization.js'
import failure from './failure.js'
import httpWithoutHeaders from './httpWithoutHeaders.js'
import refreshAccessToken from './refreshAccessToken.js'

export default async function http(config, customFailure) {
  // An API key identifies a SERVER caller and never expires, so it short-circuits
  // the operator access-token/refresh dance entirely — calling refreshAccessToken
  // in that case would try to route a headless process to /logout.
  let apiKey = getApiKey()
  let accessToken = apiKey ? null : await refreshAccessToken()

  if (!config.headers) {
    config.headers = {}
  }

  config.headers.Accept = config.headers.Accept || 'application/json'
  config.headers['Content-Type'] =
    config.headers['Content-Type'] || 'application/json'
  config.headers.Authorization =
    config.headers.Authorization ||
    (apiKey ? `Basic ${btoa(apiKey)}` : `Token ${btoa(accessToken || '')}`)
  // A per-call `chatAccessToken`/`chatEmbedOrigin` on the config takes
  // precedence over the module-global set by `setChatAuthorization`. The widget
  // passes the token that OWNS the protected customer straight from its session
  // snapshot, so a protected conversation/message write can never go out with a
  // stale-or-cleared global token (the source of the 404 NotFoundError). Callers
  // that pass nothing (operator app, mascot, intelligence) fall back to the
  // global exactly as before.
  const chatAccessToken =
    config.chatAccessToken !== undefined
      ? config.chatAccessToken
      : getChatAccessToken()
  if (chatAccessToken && !config.headers['X-Supovia-Authorization']) {
    config.headers['X-Supovia-Authorization'] = `Bearer ${chatAccessToken}`
  }
  const chatEmbedOrigin =
    config.chatEmbedOrigin !== undefined
      ? config.chatEmbedOrigin
      : getChatEmbedOrigin()
  if (chatEmbedOrigin && !config.headers['X-Supovia-Embed-Origin']) {
    config.headers['X-Supovia-Embed-Origin'] = chatEmbedOrigin
  }

  const url = `${getApiUrl()}/api/${config.url}`

  // console.log(`http ${config.method} ${url}`)

  return fetch(url, {
    method: config.method,
    headers: config.headers,
    body: JSON.stringify(config.body),
  })
    .then(response => {
      if (!response.ok) {
        if (response.url.startsWith('https://s3.amazonaws.com')) {
          return httpWithoutHeaders(response.url)
        }
        return response.json().then((error = {}) => {
          if (!error.status) {
            error.status = response.status
          }

          if (!error.message) {
            error.message = response.statusText
          }

          throw error
        })
      }
      return response.json()
    })
    .catch(error => {
      if (config.retries > 0) {
        const retried = config.retried || 0
        console.info(
          `retrying http ${
            config.method
          } ${url} after error ${error.toString()} (${
            config.retries
          } retries left)`,
        )
        let wait = 4 ** retried * 1000
        return new Promise(resolve => {
          setTimeout(resolve, wait)
        }).then(() =>
          http(
            {
              ...config,
              retries: config.retries - 1,
              retried: retried + 1,
            },
            customFailure,
          ),
        )
      }

      if (customFailure) {
        return customFailure(error)
      }
      return failure(error)
    })
}
